import {
  InteractionRequiredAuthError,
  InteractionType,
} from '@azure/msal-browser'
import {
  useAccount,
  useIsAuthenticated,
  useMsal,
  useMsalAuthentication,
} from '@azure/msal-react'
import {
  type ComponentType,
  type PropsWithChildren,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { clearSession, logoutAuthProfile } from '@app-store'
import type { EcrmMsalAuthRootState } from './types'
import { buildMsalRedirectRequest } from './msalRedirectRequest'
import { clearMsalAppSessionOnly } from './msalClient'
import {
  clearMsalAccessTokenBridge,
  consumeMsalAccessTokenFromRedirect,
} from './msalRedirectBridge'
import {
  clearMsalShellAllowedThisTabMarker,
  isMsalShellAllowedForCrossTabLoginFollowAfterLogout,
  isMsalShellAllowedThisTabAfterLogoutLogin,
} from './msalTabShellGate'

export interface MsalAuthGuardProps extends PropsWithChildren {
  /** Scopes for redirect + silent acquisition (memoize in the app for stable identity). */
  scopes: string[]
  msalInstance: import('@azure/msal-browser').PublicClientApplication
  LoginAttemptComponent: ComponentType<{
    token?: string | null
    text: string
    idToken?: string | null
    refreshToken?: string | null
  }>
}

/**
 * Redirects protected routes to `/logout` when the profile is explicitly logged out
 * and this tab did not press Login after logout (`sessionStorage` marker).
 */
function MsalLogoutRedirect() {
  useLayoutEffect(() => {
    const url = `${window.location.origin}/logout`
    window.location.replace(url)
  }, [])
  return null
}

/**
 * Inner guard: invokes MSAL redirects / silent flows only after cross-tab logout check passes.
 */
function InnerMsalAuthGuard({
  children,
  scopes,
  msalInstance,
  LoginAttemptComponent,
}: MsalAuthGuardProps) {
  const dispatch = useDispatch()
  const forceMsalPromptNextLogin = useSelector(
    (s: EcrmMsalAuthRootState) => !!s.session.forceMsalPromptNextLogin
  )
  const profileExplicitLogout = useSelector(
    (s: EcrmMsalAuthRootState) => s.authUser.isAuthenticated === false
  )
  const crossTabLoginFollow =
    isMsalShellAllowedForCrossTabLoginFollowAfterLogout()
  const requireInteractiveLoginPrompt = useMemo(
    () =>
      crossTabLoginFollow
        ? false
        : forceMsalPromptNextLogin || profileExplicitLogout,
    [crossTabLoginFollow, forceMsalPromptNextLogin, profileExplicitLogout]
  )
  const redirectRequest = useMemo(
    () => buildMsalRedirectRequest(requireInteractiveLoginPrompt, scopes),
    [requireInteractiveLoginPrompt, scopes]
  )

  const { error } = useMsalAuthentication(
    InteractionType.Redirect,
    redirectRequest
  )
  const { accounts, instance } = useMsal()
  const account = useAccount(accounts[0] || null)
  const isMsalAuthenticated = useIsAuthenticated()
  const isAppAuthenticated = useSelector(
    (s: EcrmMsalAuthRootState) => s.session.isAuthenticated
  )
  const backendSessionEstablished = useSelector(
    (s: EcrmMsalAuthRootState) =>
      !!(s.session.isAuthenticated && s.session.token)
  )
  const [accessToken, setAccessToken] = useState<string | null>(null)

  useEffect(() => {
    if (backendSessionEstablished) {
      clearMsalShellAllowedThisTabMarker()
    }
  }, [backendSessionEstablished])

  useEffect(() => {
    if (!account) return

    const fromRedirect = consumeMsalAccessTokenFromRedirect()
    if (fromRedirect) {
      setAccessToken(fromRedirect)
      return
    }

    if (accessToken) return

    // if (profileExplicitLogout) {
    //   const request = {
    //     ...buildMsalRedirectRequest(true, scopes),
    //     account,
    //   }
    //   void instance.acquireTokenRedirect(request)
    //   return
    // }

    const silentRequest = { scopes, account }

    void instance
      .acquireTokenSilent(silentRequest)
      .then((response) => {
        setAccessToken(response.accessToken)
      })
      .catch(async (err) => {
        if (err instanceof InteractionRequiredAuthError) {
          const request = {
            ...buildMsalRedirectRequest(requireInteractiveLoginPrompt, scopes),
            account,
          }
          void instance.acquireTokenRedirect(request)
          return
        }
        console.error('MSAL acquireTokenSilent failed:', err)
        await clearMsalAppSessionOnly(msalInstance)
        clearMsalAccessTokenBridge()
        dispatch(logoutAuthProfile())
        dispatch(clearSession())
        window.location.assign(window.location.origin + '/logout')
      })
  }, [
    account,
    instance,
    accessToken,
    dispatch,
    requireInteractiveLoginPrompt,
    scopes,
    msalInstance,
  ])

  useEffect(() => {
    if (!error) return
    console.error('MSAL Authentication Error:', error)
    void (async () => {
      await clearMsalAppSessionOnly(msalInstance)
      clearMsalAccessTokenBridge()
      dispatch(logoutAuthProfile())
      dispatch(clearSession())
      window.location.assign(window.location.origin + '/logout')
    })()
  }, [error, dispatch, msalInstance])

  if (isMsalAuthenticated && isAppAuthenticated) {
    return children
  }
  if (isMsalAuthenticated && !isAppAuthenticated) {
    return (
      <LoginAttemptComponent token={accessToken} text={'Authenticating...'} />
    )
  }
  return null
}

/**
 * MSAL Entra guard: redirect-based `useMsalAuthentication`, silent token with redirect fallback
 * (aligned with agent-desk). Requires `MsalProvider` at app root and shared `msalInstance`.
 *
 * Explicit logout persists on `authUser` (localStorage). Only the tab that chose Login sets
 * `sessionStorage`, so parallel tabs hitting `/` while IdP interactive login is unfinished
 * remain on `/logout` instead of silently signing in via MSAL SSO.
 */
export function MsalAuthGuard(props: MsalAuthGuardProps) {
  const authExplicitlyLoggedOut = useSelector(
    (s: EcrmMsalAuthRootState) => s.authUser.isAuthenticated === false
  )
  const shellAllowedThisTab = isMsalShellAllowedThisTabAfterLogoutLogin()
  const crossTabLoginFollow =
    isMsalShellAllowedForCrossTabLoginFollowAfterLogout()

  if (authExplicitlyLoggedOut && !shellAllowedThisTab && !crossTabLoginFollow) {
    return <MsalLogoutRedirect />
  }

  return <InnerMsalAuthGuard {...props} />
}
